import { assert, assertNever, CheckedAddress } from '@sparkdotfi/common-universal'
import { QueryKey } from '@tanstack/react-query'

import { allowanceQueryKey } from '../../approve/logic/query'
import { SwapAction } from '../types'
import { getSwapActionPath } from './getSwapActionPath'
import { SPARK_UI_REFERRAL_CODE_BIGINT } from '@/config/consts'
import {
  dssLitePsmConfig,
  migrationActionsConfig,
  psm3Abi,
  psm3Address,
  usdsPsmWrapperConfig,
} from '@/config/contracts-generated'
import { getContractAddress } from '@/domain/hooks/useContractAddress'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { getBalancesQueryKeyPrefix } from '@/domain/wallet/getBalancesQueryKeyPrefix'
import { ensureConfigTypes } from '@/features/actions/logic/transactions/useWrite'
import { ActionConfig, ActionContext } from '@/features/actions/logic/types'

export function createSwapActionConfig(action: SwapAction, context: ActionContext): ActionConfig {
  const { account, chainId, tokenRepository, marketInfo } = context
  const tokenLookup = tokenRepository ?? marketInfo
  assert(tokenLookup, 'Token repository or market info is required for swap action')
  const usdc = tokenLookup.findOneBySymbol(TokenSymbol('USDC'))

  const actionPath = getSwapActionPath({
    from: action.from,
    to: action.to,
    chainId,
  })

  return {
    getWriteConfig: () => {
      switch (actionPath) {
        case 'dai-usds': {
          const amount = action.from.toBigInt(action.amount)
          return ensureConfigTypes({
            address: getContractAddress(migrationActionsConfig.address, chainId),
            abi: migrationActionsConfig.abi,
            functionName: 'migrateDAIToUSDS',
            args: [account, amount],
          })
        }
        case 'usds-dai': {
          const amount = action.from.toBigInt(action.amount)
          return ensureConfigTypes({
            address: getContractAddress(migrationActionsConfig.address, chainId),
            abi: migrationActionsConfig.abi,
            functionName: 'downgradeUSDSToDAI',
            args: [account, amount],
          })
        }
        case 'usdc-dai':
        case 'dai-usdc': {
          const usdcAmount = usdc.toBigInt(action.amount)
          return ensureConfigTypes({
            address: getContractAddress(dssLitePsmConfig.address, chainId),
            abi: dssLitePsmConfig.abi,
            functionName: action.to.symbol === usdc.symbol ? 'buyGem' : 'sellGem',
            args: [account, usdcAmount],
          })
        }

        case 'usdc-usds':
        case 'usds-usdc': {
          const usdcAmount = usdc.toBigInt(action.amount)
          return ensureConfigTypes({
            address: getContractAddress(usdsPsmWrapperConfig.address, chainId),
            abi: usdsPsmWrapperConfig.abi,
            functionName: action.to.symbol === usdc.symbol ? 'buyGem' : 'sellGem',
            args: [account, usdcAmount],
          })
        }

        case 'base-usdc-usds':
        case 'base-usds-usdc':
        case 'arbitrum-usdc-usds':
        case 'arbitrum-usds-usdc':
        case 'optimism-usdc-usds':
        case 'optimism-usds-usdc':
        case 'unichain-usdc-usds':
        case 'unichain-usds-usdc': {
          const assetIn = action.from.address
          const assetOut = action.to.address
          const amountIn = action.from.toBigInt(action.amount)
          const minAmountOut = action.to.toBigInt(action.amount)
          const receiver = account

          return ensureConfigTypes({
            address: getContractAddress(psm3Address, chainId),
            abi: psm3Abi,
            functionName: 'swapExactIn',
            args: [assetIn, assetOut, amountIn, minAmountOut, receiver, SPARK_UI_REFERRAL_CODE_BIGINT],
          })
        }
        default:
          assertNever(actionPath)
      }
    },

    invalidates: () => {
      const balancesQueryKeyPrefix = getBalancesQueryKeyPrefix({ chainId, account })
      function getAllowanceQueryKey(spender: CheckedAddress): QueryKey {
        return allowanceQueryKey({ token: action.from.address, spender, account, chainId })
      }

      switch (actionPath) {
        case 'dai-usds':
        case 'usds-dai':
          return [
            balancesQueryKeyPrefix,
            getAllowanceQueryKey(getContractAddress(migrationActionsConfig.address, chainId)),
          ]

        case 'usdc-dai':
        case 'dai-usdc':
          return [balancesQueryKeyPrefix, getAllowanceQueryKey(getContractAddress(dssLitePsmConfig.address, chainId))]

        case 'usdc-usds':
        case 'usds-usdc':
          return [
            balancesQueryKeyPrefix,
            getAllowanceQueryKey(getContractAddress(usdsPsmWrapperConfig.address, chainId)),
          ]

        case 'base-usdc-usds':
        case 'base-usds-usdc':
        case 'arbitrum-usdc-usds':
        case 'arbitrum-usds-usdc':
        case 'optimism-usdc-usds':
        case 'optimism-usds-usdc':
        case 'unichain-usdc-usds':
        case 'unichain-usds-usdc':
          return [balancesQueryKeyPrefix, getAllowanceQueryKey(getContractAddress(psm3Address, chainId))]

        default:
          assertNever(actionPath)
      }
    },
  }
}
